import type { UnitInfoMobileJawInterface } from 'store/units/types';
import type { UnitConfigParams } from 'unitData/BaseUnitConfig';
import { BaseUnitConfig } from 'unitData/BaseUnitConfig';
import type { MobileJawUnitInfoDataInterface } from './unitInfoData';
import { UnitTypes } from 'unitData/types';

export class MobileJawConfig extends BaseUnitConfig {
  static getDefaultData(params?: UnitConfigParams): UnitInfoMobileJawInterface {
    const unitInfoData =
      (params?.data as MobileJawUnitInfoDataInterface | undefined) ?? {};
    const type = params?.unitType ?? UnitTypes.MOBILE_TRACKED_JAW;

    if (type === UnitTypes.MOBILE_TRACKED_JAW) {
      const defaultModel = Object.entries(unitInfoData).find(
        (e) => e[0] === 'QJ241',
      );
      if (!defaultModel) {
        throw new Error('Unable to find data for QJ241');
      }
      const model = defaultModel[0];
      const modelData = defaultModel[1];

      return {
        modelName: model,
        configuration: modelData.configurations.configurationOptions[0],
        numberOfPorts: 1,
        JAW1: {
          modelName: modelData.configurations.JAW1.model,
          css: modelData.configurations.JAW1.css.defaultCss,
          moisture: 0,
          newMoisture: false,
        },
        GRIZZLY_FEEDER1: {
          modelName: modelData.configurations?.GRIZZLY_FEEDER1?.model ?? {
            simulationModel: '',
            displayName: '',
          },
          decks: {
            0: {
              separationSize:
                model === 'QJ241' || model === 'QJ341'
                  ? modelData.configurations?.GRIZZLY_FEEDER1?.decks[0]
                      .grizzlyTypeOptions[0].defaultSeparationSize
                  : modelData.configurations?.GRIZZLY_FEEDER1?.decks[0]
                      .defaultSeparation,
              grizzlyType:
                modelData.configurations?.GRIZZLY_FEEDER1?.decks[0]
                  .defaultGrizzlyType,
            },
            1: {
              separationSize: 25,
            },
          },
        },
        GRIZZLY_FEEDER2: {
          modelName: modelData.configurations?.GRIZZLY_FEEDER2?.model ?? {
            simulationModel: '',
            displayName: '',
          },
          decks: {
            0: {
              separationSize:
                modelData.configurations?.GRIZZLY_FEEDER2?.decks[0]
                  .defaultSeparation,
            },
            1: {
              separationSize:
                modelData.configurations?.GRIZZLY_FEEDER2?.decks[1]
                  .defaultSeparation,
            },
          },
        },
        SCREEN1: {
          model: modelData.configurations?.SCREEN1?.model.displayName ?? '',
          decks: {
            0: {
              separationSize:
                modelData.configurations?.SCREEN1?.decks[0]
                  .defaultSeparationSize,
            },
          },
        },
        moisture: 0,
        newMoisture: false,
        display: {
          ...BaseUnitConfig.getDefaultDisplay(),
          displayLoad: true,
          displayConfig: false,
          displayCrusherModel: false,
          displayCrusherCss: false,
          displayFeederModel: false,
          displayGrizzlySeparationSize: false,
          displayScreenSeparationSize: false,
          displayHSSeparationSize: false,
          displayFFSeparationSize: false,
        },
        parallelUnits: 1,
      };
    } else {
      const defaultModel = Object.entries(unitInfoData).find(
        (e) => e[0] === 'UJ210',
      );
      if (!defaultModel) {
        throw new Error('Unable to find data for UJ210');
      }
      const model = defaultModel[0];
      const modelData = defaultModel[1];

      return {
        modelName: model,
        configuration: modelData.configurations.configurationOptions[0],
        numberOfPorts: 1,
        JAW1: {
          modelName: modelData.configurations.JAW1.model,
          css: modelData.configurations.JAW1.css.defaultCss,
          moisture: 0,
          newMoisture: false,
        },
        GRIZZLY_FEEDER1: {
          modelName: modelData.configurations?.GRIZZLY_FEEDER1?.model ?? {
            simulationModel: '',
            displayName: '',
          },
          decks: {
            0: {
              separationSize:
                model === 'QJ241' || model === 'QJ341'
                  ? modelData.configurations?.GRIZZLY_FEEDER1?.decks[0]
                      .grizzlyTypeOptions[0].defaultSeparationSize
                  : modelData.configurations?.GRIZZLY_FEEDER1?.decks[0]
                      .defaultSeparation,
              grizzlyType:
                modelData.configurations?.GRIZZLY_FEEDER1?.decks[0]
                  .defaultGrizzlyType,
            },
            1: {
              separationSize:
                modelData.configurations?.GRIZZLY_FEEDER1?.decks[1]
                  .defaultSeparation,
            },
          },
        },
        GRIZZLY_FEEDER2: {
          modelName: modelData.configurations?.GRIZZLY_FEEDER2?.model ?? {
            simulationModel: '',
            displayName: '',
          },
          decks: {
            0: {
              separationSize:
                modelData.configurations?.GRIZZLY_FEEDER2?.decks[0]
                  .defaultSeparation,
            },
            1: {
              separationSize:
                modelData.configurations?.GRIZZLY_FEEDER2?.decks[1]
                  .defaultSeparation,
            },
          },
        },
        newMoisture: false,
        display: {
          ...BaseUnitConfig.getDefaultDisplay(),
          displayLoad: true,
          displayConfig: false,
          displayCrusherModel: false,
          displayCrusherCss: false,
          displayFeederModel: false,
          displayGrizzlySeparationSize: false,
          displayScreenSeparationSize: false,
          displayHSSeparationSize: false,
          displayFFSeparationSize: false,
        },
        parallelUnits: 1,
        moisture: 0,
      };
    }
  }

  static getNumberOfPorts(model?: string, config?: number): number {
    if (!model || config === undefined) {
      return 1;
    }

    switch (model) {
      case 'QJ241':
      case 'QJ341':
      case 'UJ440E':
      case 'UJ443EVGF':
      case 'UJ443EVGFUS':
      case 'UJ640EVGFUS':
        switch (config) {
          case 0:
            return 1;
          case 1:
            return 2;
          default:
            return 1;
        }
      case 'QJ341+':
      case 'UJ210':
      case 'UJ300':
      case 'UJ310':
      case 'UJ313':
        switch (config) {
          case 0:
            return 1;
          case 1:
            return 2;
          case 2:
            return 2;
          default:
            return 1;
        }
      case 'UJ440IVGF':
      case 'UJ440IVGFUS':
        switch (config) {
          case 0:
            return 1;
          case 1:
            return 2;
          case 2:
            return 2;
          case 3:
            return 3;
          default:
            return 1;
        }
      case 'UJ473':
        return config < 2 ? 3 : 2;
      default:
        return 1;
    }
  }
}

export const mobileJawConfig = new MobileJawConfig();

export const getNumberOfPorts = (model: string, configKey: number): number => {
  return mobileJawConfig.getNumberOfPorts(model, configKey);
};
